import { parseDay } from "./date";
import { dayMark } from "./dayMarks";
import { hasCondition, older } from "./eating";
import { dayValue, nutrientAverages, topSources, windowDates } from "./foodTrends";
import { basedOn, windowWord, type Insight, type Window } from "./insight";
import { entriesFor } from "./intake";
import { SAT_FAT_ENERGY_MAX, SODIUM_MAX_MG, dailyTargets, type DailyTargets } from "./nutrition";
import type { DayTargets, FoodEntry, KayaState } from "./types";

/**
 * Nutrient cover: how much of what five nutrients need a food, a day or a window of days carries,
 * against the person's own targets and budget. (ADR-040, plan §3.3, nutrition-scores.md §6 C)
 *
 * It is the Nutrient Balance Concept (Fern and Drewnowski, 2015) on the five nutrients Kaya logs,
 * with one change: eating under budget never raises a day's cover, because under-eating is the
 * risk after 60, not the virtue. It is a share inside a sentence — never a grade, a colour or a
 * verdict on a food.
 *
 * - A food: `q_i = (n_i / T_i) / (kcal / B)` — how many times its share of the budget's energy the
 *   food gives of the need for nutrient i. Shown as words, never a number.
 * - A day: `a_i = min(1, (n_i / T_i) × f)` with `f = B / max(E, B)`; cover = 100 × the mean of the
 *   known `a_i`.
 * - A window: each nutrient's capped daily share averaged over the days it is known, times the
 *   window's budget factor (its average In against its average budget: a day over and a day under
 *   are one week's eating, and that is how the worked figures were done).
 *
 * Saturated fat and sodium stand alongside, never subtracted. A nutrient not known is left out and
 * named; with fewer than three of five known there is no cover at all.
 */

export type CoverKey = "p" | "fb" | "ca" | "fe" | "pot";
export const COVER_KEYS: CoverKey[] = ["p", "fb", "ca", "fe", "pot"];

export const COVER_META: Record<CoverKey, { label: string; word: string; unit: "g" | "mg" }> = {
  p: { label: "Protein", word: "protein", unit: "g" },
  fb: { label: "Fibre", word: "fibre", unit: "g" },
  ca: { label: "Calcium", word: "calcium", unit: "mg" },
  fe: { label: "Iron", word: "iron", unit: "mg" },
  pot: { label: "Potassium", word: "potassium", unit: "mg" },
};

/** Below three of five the mean says more about what is missing than about the food. */
export const MIN_KNOWN = 3;
/** Every food insight waits for a week of logged days. */
export const MIN_COVER_DAYS = 7;
/** A food this light is mostly water; a ratio per calorie on it says nothing about a portion. */
export const MIN_KCAL_PER_100G = 10;
/** Saturated fat gets a clause past 1.5 times its share (plan §3.3). */
export const SAT_FAT_CLAUSE = 1.5;
/**
 * Sodium needs three times its share. A cooked dish's sodium is the salt its recipe assumed —
 * USDA's "boiled, with salt" rows put boiled dal at 1.9 times its share and palak at 2.7 — and
 * every kitchen salts differently; a pickle, papad or namkeen is far past three wherever it is made.
 */
export const SODIUM_CLAUSE = 3;
/** Past this a limit is "several times its share" rather than "above". */
export const LIMIT_SEVERAL = 4;

export type CoverTargets = Omit<DayTargets, "at">;

export function coverTargets(T: DailyTargets): CoverTargets {
  return { p: T.protein, fb: T.fibre, ca: T.calcium, fe: T.iron, pot: T.potassium, na: T.sodiumMax };
}

/** Today's targets, stamped — what `addFoodEntry` freezes into the day. */
export function freezeTargets(S: KayaState, now = new Date()): DayTargets {
  // The nutrient targets do not depend on the day's activity; 0 skips working it out.
  return { at: now.getTime(), ...coverTargets(dailyTargets(S, now, 0)) };
}

const num = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

function frozen(t: DayTargets | undefined): CoverTargets | null {
  if (!t || !num(t.p) || !num(t.ca) || !num(t.pot) || !num(t.na)) return null;
  return {
    p: t.p,
    fb: num(t.fb) ? t.fb : null,
    ca: t.ca,
    fe: num(t.fe) ? t.fe : null,
    pot: t.pot,
    na: t.na,
  };
}

/** The day's own targets when they were frozen; otherwise today's rules on that date. */
export function targetsOn(S: KayaState, ds: string): CoverTargets {
  return frozen(S.logs[ds]?.targets) ?? coverTargets(dailyTargets(S, parseDay(ds), 0));
}

/* ---------- a food ---------- */

/** Per 100 g, as the food table and a custom food carry it. */
export interface Per100 {
  k: number;
  p: number;
  fb?: number;
  ca?: number;
  fe?: number;
  pot?: number;
  sf?: number;
  na?: number;
}

export type ShareBand = "none" | "mostly" | "half" | "under" | "share" | "twice" | "several";

const BAND_WORDS: Record<ShareBand, string> = {
  none: "energy only",
  mostly: "mostly energy",
  half: "about half its share",
  under: "a little under its share",
  share: "about its share",
  twice: "about twice its share",
  several: "several times its share",
};

/** Bands at or above "about its share" name what the food brings; below it, naming a nutrient
 *  would read as praise for a food that carries less than its weight. */
const NAMES_FROM: ShareBand[] = ["share", "twice", "several"];

export function bandOf(qi: number): ShareBand {
  if (qi < 0.1) return "none";
  if (qi < 0.45) return "mostly";
  if (qi < 0.7) return "half";
  if (qi < 0.9) return "under";
  if (qi < 1.4) return "share";
  if (qi < 3) return "twice";
  return "several";
}

export interface LimitClause {
  key: "sf" | "na";
  ratio: number;
  several: boolean;
}

export interface FoodCover {
  /** the mean of the known `q_i` — never shown */
  qi: number;
  band: ShareBand;
  q: Partial<Record<CoverKey, number>>;
  /** at most two, strongest first, each at least its share */
  strongest: CoverKey[];
  limits: LimitClause[];
}

/**
 * What a food brings per calorie at this person's budget, or null when there is nothing honest to
 * say: no budget (no body numbers), a food too light to judge per calorie, or fewer than three of
 * the five nutrients known.
 */
export function foodCover(f: Per100, T: CoverTargets, budget: number | null): FoodCover | null {
  if (budget === null || !(budget > 0) || !num(f.k) || f.k < MIN_KCAL_PER_100G) return null;
  const energy = f.k / budget;
  const q: Partial<Record<CoverKey, number>> = {};
  for (const k of COVER_KEYS) {
    const n = f[k];
    const t = T[k];
    if (!num(n) || t === null || !(t > 0)) continue;
    q[k] = n / t / energy;
  }
  const known = COVER_KEYS.filter((k) => q[k] !== undefined);
  if (known.length < MIN_KNOWN) return null;
  const qi = known.reduce((a, k) => a + (q[k] ?? 0), 0) / known.length;
  const band = bandOf(qi);
  const strongest = NAMES_FROM.includes(band)
    ? known
        .filter((k) => (q[k] ?? 0) >= 1)
        .sort((a, b) => (q[b] ?? 0) - (q[a] ?? 0))
        .slice(0, 2)
    : [];

  const limits: LimitClause[] = [];
  // Saturated fat's share is its share of energy against the 10 % ceiling, whatever the budget.
  if (num(f.sf)) {
    const ratio = (f.sf * 9) / f.k / SAT_FAT_ENERGY_MAX;
    if (ratio > SAT_FAT_CLAUSE) limits.push({ key: "sf", ratio, several: ratio >= LIMIT_SEVERAL });
  }
  if (num(f.na) && T.na > 0) {
    const ratio = f.na / T.na / energy;
    if (ratio > SODIUM_CLAUSE) limits.push({ key: "na", ratio, several: ratio >= LIMIT_SEVERAL });
  }
  return { qi, band, q, strongest, limits };
}

const LIMIT_WORD: Record<LimitClause["key"], string> = { sf: "sat fat", na: "sodium" };

function andList(xs: string[]): string {
  if (xs.length <= 1) return xs.join("");
  return `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;
}

function limitWords(limits: LimitClause[]): string {
  if (!limits.length) return "";
  if (limits.length === 2 && limits[0].several === limits[1].several)
    return `${LIMIT_WORD.sf} and ${LIMIT_WORD.na} ${limits[0].several ? "several times" : "above"} their share`;
  return limits.map((l) => `${LIMIT_WORD[l.key]} ${l.several ? "several times" : "above"} its share`).join(", ");
}

/**
 * The words for one food (SNIPPETS §29). `long` spells out what the share is of ("… its share of
 * your needs per calorie"); `bare` drops "its share" for a row that repeats the band above it.
 */
export function coverWords(c: FoodCover, form: "long" | "short" | "bare" = "short"): string {
  let s = BAND_WORDS[c.band];
  if (form === "long" && s.endsWith("its share")) s += " of your needs per calorie";
  else if (form === "bare" && (c.band === "twice" || c.band === "several")) s = s.replace(/ its share$/, "");
  if (c.strongest.length) s += ` — ${andList(c.strongest.map((k) => COVER_META[k].word))}`;
  const lim = limitWords(c.limits);
  return lim ? `${s}; ${lim}` : s;
}

/**
 * The third line of each row in a result list: the first row with a share says what the share is
 * of, a row repeating the band above it says only the band, and a row with nothing to say has no
 * line. The words are the same everywhere else.
 */
export function coverLines(list: (FoodCover | null)[]): (string | null)[] {
  let spelled = false;
  let prev: ShareBand | null = null;
  return list.map((c) => {
    if (!c) {
      prev = null;
      return null;
    }
    let form: "long" | "short" | "bare" = "short";
    if (!spelled && BAND_WORDS[c.band].endsWith("its share")) {
      form = "long";
      spelled = true;
    } else if (prev === c.band) form = "bare";
    prev = c.band;
    return coverWords(c, form);
  });
}

/* ---------- a portion ---------- */

export interface PortionShare {
  key: CoverKey;
  label: string;
  unit: "g" | "mg";
  /** null when the entry does not carry it (invariant 18) */
  value: number | null;
  target: number | null;
  /** value ÷ target, uncapped; null when either is unknown */
  share: number | null;
}

/** What one portion gives of each of the day's five needs. */
export function portionShares(e: Pick<FoodEntry, CoverKey>, T: CoverTargets): PortionShare[] {
  return COVER_KEYS.map((key) => {
    const v = e[key];
    const value = num(v) ? v : null;
    const target = T[key];
    const share = value !== null && target !== null && target > 0 ? value / target : null;
    return { key, label: COVER_META[key].label, unit: COVER_META[key].unit, value, target, share };
  });
}

/* ---------- a day ---------- */

export interface DayCover {
  /** 0–100, or null — no budget, nothing logged, or under three nutrients known */
  cover: number | null;
  /** the budget factor, 1 on or under budget */
  f: number | null;
  kcal: number;
  budget: number | null;
  value: Record<CoverKey, number | null>;
  /** value ÷ target capped at 1, before the budget factor */
  share: Record<CoverKey, number | null>;
  known: CoverKey[];
  targets: CoverTargets;
}

export function dayCover(entries: FoodEntry[], T: CoverTargets, budget: number | null): DayCover {
  const kcal = entries.reduce((a, e) => a + (num(e.kcal) ? e.kcal : 0), 0);
  const value = {} as Record<CoverKey, number | null>;
  const share = {} as Record<CoverKey, number | null>;
  const known: CoverKey[] = [];
  for (const k of COVER_KEYS) {
    const v = dayValue(entries, k);
    const t = T[k];
    value[k] = v;
    share[k] = v !== null && t !== null && t > 0 ? Math.min(1, v / t) : null;
    if (share[k] !== null) known.push(k);
  }
  const ok = budget !== null && budget > 0 && entries.length > 0;
  const f = ok ? budget / Math.max(kcal, budget) : null;
  const cover =
    f !== null && known.length >= MIN_KNOWN
      ? (100 * known.reduce((a, k) => a + Math.min(1, (value[k] ?? 0) / (T[k] ?? 1) * f), 0)) / known.length
      : null;
  return { cover, f, kcal, budget: ok ? budget : null, value, share, known, targets: T };
}

/* ---------- a window ---------- */

export interface CoverBar {
  key: CoverKey;
  label: string;
  word: string;
  unit: "g" | "mg";
  /** mean of the daily capped shares, 0–1, over the days it is known */
  share: number | null;
  /** mean amount a day, over the same days */
  avg: number | null;
  /** mean of those days' targets */
  target: number | null;
  known: number;
}

export interface WindowCover {
  /** days in the window */
  of: number;
  /** logged days */
  logged: number;
  /** logged days with a budget and three nutrients known — the days the cover rests on */
  days: number;
  /** 0–100 */
  cover: number | null;
  /** the same before the budget factor */
  unweighted: number | null;
  f: number | null;
  inAvg: number | null;
  budgetAvg: number | null;
  bars: CoverBar[];
  /** the nutrient furthest behind, or null when every known one is met */
  furthest: CoverBar | null;
  /** days calcium, iron and potassium are all known on */
  mineralDays: number;
  /** nutrients known on none of the days */
  unknown: CoverKey[];
  /** logged days, and not one with a budget: the body numbers are missing */
  noBudget: boolean;
  /** under a week of days: the figure is not yet a claim */
  ready: boolean;
}

const mean = (xs: number[]): number | null => (xs.length ? xs.reduce((a, x) => a + x, 0) / xs.length : null);

export function windowCover(
  S: KayaState,
  today: string,
  w: Window,
  budget: (ds: string) => number | null
): WindowCover {
  const dates = windowDates(S, today, w);
  let logged = 0;
  let budgeted = 0;
  const days: DayCover[] = [];
  for (const ds of dates) {
    // complete days only, as every other window figure (ADR-049)
    if (dayMark(S, ds)) continue;
    const entries = entriesFor(S, ds);
    if (!entries.length) continue;
    logged++;
    const d = dayCover(entries, targetsOn(S, ds), budget(ds));
    if (d.f !== null) budgeted++;
    if (d.f !== null && d.known.length >= MIN_KNOWN) days.push(d);
  }

  const bars: CoverBar[] = COVER_KEYS.map((key) => {
    const on = days.filter((d) => d.share[key] !== null);
    return {
      key,
      label: COVER_META[key].label,
      word: COVER_META[key].word,
      unit: COVER_META[key].unit,
      share: mean(on.map((d) => d.share[key] ?? 0)),
      avg: mean(on.map((d) => d.value[key] ?? 0)),
      target: mean(on.map((d) => d.targets[key] ?? 0)),
      known: on.length,
    };
  });
  const knownBars = bars.filter((b) => b.share !== null);
  const inAvg = mean(days.map((d) => d.kcal));
  const budgetAvg = mean(days.map((d) => d.budget ?? 0));
  const f = inAvg !== null && budgetAvg !== null && budgetAvg > 0 ? budgetAvg / Math.max(inAvg, budgetAvg) : null;
  const unweighted = knownBars.length >= MIN_KNOWN ? 100 * (mean(knownBars.map((b) => b.share ?? 0)) ?? 0) : null;
  const cover = unweighted !== null && f !== null ? unweighted * f : null;
  const lowest = [...knownBars].sort((a, b) => (a.share ?? 0) - (b.share ?? 0))[0];
  const mineralDays = days.filter((d) => d.value.ca !== null && d.value.fe !== null && d.value.pot !== null).length;

  return {
    of: dates.length,
    logged,
    days: days.length,
    cover,
    unweighted,
    f,
    inAvg,
    budgetAvg,
    bars,
    furthest: lowest && (lowest.share ?? 1) < 1 ? lowest : null,
    mineralDays,
    unknown: days.length ? bars.filter((b) => b.share === null).map((b) => b.key) : [],
    noBudget: logged > 0 && budgeted === 0,
    ready: days.length >= MIN_COVER_DAYS && cover !== null,
  };
}

/* ---------- the sentences ---------- */

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");
/** Grams and milligrams under 10 to a tenth ("9.4 of 19 mg"), whole above. */
export const coverAmount = (n: number) => (n < 10 ? (Math.round(n * 10) / 10).toLocaleString("en-US") : fmt(n));
const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

/** "protein, fibre, calcium, iron and potassium" — the nutrients the figure rests on. */
export function coverNames(wc: WindowCover): string {
  return andList(wc.bars.filter((b) => b.share !== null).map((b) => b.word));
}

function mineralsPart(wc: WindowCover): string {
  if (wc.mineralDays === 0) return "no minerals on record";
  if (wc.days === 1) return "minerals on record";
  return wc.mineralDays === wc.days ? `minerals on all ${wc.days}` : `minerals on ${wc.mineralDays} of ${wc.days}`;
}

/** The chapter's headline insight: the figure in a sentence, the furthest behind named. */
export function coverInsight(wc: WindowCover, w: Window): Insight {
  const win = windowWord(w);
  const daysWord = `${wc.days} logged ${plural(wc.days, "day")}`;
  if (wc.noBudget) {
    return {
      key: "food.cover",
      text: "Nutrient cover needs your year of birth and sex, which set the budget and the targets.",
      basedOn: basedOn(`${wc.logged} logged ${plural(wc.logged, "day")}`),
      wait: "after your body numbers are in",
    };
  }
  if (!wc.ready) {
    const need = Math.max(1, MIN_COVER_DAYS - wc.days);
    return {
      key: "food.cover",
      text: `Nutrient cover appears after ${MIN_COVER_DAYS} logged days.`,
      basedOn: basedOn(daysWord),
      wait: `after ${need} more logged ${plural(need, "day")}`,
    };
  }
  const pct = Math.round(wc.cover ?? 0);
  let text = `Your logged days covered ${pct} % of what ${coverNames(wc)} need`;
  const b = wc.furthest;
  text +=
    b && b.avg !== null && b.target !== null
      ? `; ${b.word} is furthest behind at ${coverAmount(b.avg)} of ${coverAmount(b.target)} ${b.unit}.`
      : ".";
  const unknown = wc.unknown.length ? `${andList(wc.unknown.map((k) => COVER_META[k].word))} not known` : null;
  return { key: "food.cover", text, basedOn: basedOn(daysWord, win, mineralsPart(wc), unknown) };
}

/** When the window ate over its budget, how much that took off — the budget factor, in words. */
export function coverBudgetInsight(wc: WindowCover): Insight | null {
  if (!wc.ready || wc.f === null || wc.f >= 1 || wc.inAvg === null || wc.budgetAvg === null) return null;
  const less = Math.round(100 * (1 - wc.f));
  if (less < 1) return null;
  const budget = Math.round(wc.budgetAvg / 10) * 10;
  const over = wc.inAvg / wc.budgetAvg - 1 <= 0.1 ? "a little over" : "over";
  return {
    key: "food.coverBudget",
    text: `In averaged ${fmt(wc.inAvg)} kcal a day on logged days, ${over} the ~${fmt(budget)} budget, so the cover counts ${less} % less.`,
    basedOn: basedOn(`${wc.days} logged ${plural(wc.days, "day")}`),
    tag: "est",
  };
}

/** "Moong dal (split), cooked" → "moong dal": a list reads as foods, not labels. */
function shortName(name: string): string {
  const s = name.split(/[,(/]/)[0].trim();
  return s.charAt(0).toLowerCase() + s.slice(1);
}

/**
 * Saturated fat and sodium, beside the figure and never netted against it. A limit that is over
 * comes first with its ceiling; sodium names its top two sources when it is over and saturated fat
 * is not — the weakest-nutrient row already names saturated fat's.
 */
export function coverLimitsLine(S: KayaState, today: string, w: Window): string | null {
  const na = nutrientAverages(S, today, w);
  const sfPct = na.satFatPct;
  const sodium = na.by.na.avg;
  const sfOver = sfPct !== null && sfPct > na.satFatMaxPct;
  const naOver = sodium !== null && sodium > SODIUM_MAX_MG;
  const parts: { over: boolean; text: string }[] = [];
  if (sfPct !== null) {
    const pct = Math.round(sfPct);
    parts.push({
      over: sfOver,
      text: `saturated fat ${pct} % of energy${sfOver ? ` (keep under ${na.satFatMaxPct} %)` : ""}`,
    });
  }
  if (sodium !== null) {
    let text = `sodium ${fmt(sodium)} mg`;
    if (naOver) {
      text += ` (limit ${fmt(SODIUM_MAX_MG)}${hasCondition(S, "bp") ? " for high BP" : ""})`;
      if (!sfOver) {
        const src = [...new Set(topSources(S, today, w, "na").map((s) => shortName(s.name)))].slice(0, 2);
        if (src.length === 2) text += ` — ${src[0]} and ${src[1]} are the top two`;
        else if (src.length === 1) text += ` — ${src[0]} is the top one`;
      }
    }
    parts.push({ over: naOver, text });
  }
  if (!parts.length) return null;
  const ordered = [...parts.filter((p) => p.over), ...parts.filter((p) => !p.over)];
  return `Alongside, not subtracted: ${ordered.map((p) => p.text).join(" · ")}`;
}

/** What five nutrients cannot say, once, under the bars. After 60 it is B12 by name. */
export function coverCannotSee(S: KayaState, today = new Date()): string {
  return older(S, today)
    ? "What this cannot see: vitamin B12 — it is not in the table for dishes, and after 60 it is worth asking your doctor about. Nor sugar or food groups."
    : "What this cannot see: vitamins, sugar, food groups — the table does not carry them";
}
